// Members: who is on the team, their status, and the shield icon that opens
// their authority. Tick several people to give them the same authority at once.
import React, { useMemo, useState } from 'react';
import { ShieldCheck, UserPlus, MoreHorizontal, Send, PauseCircle, PlayCircle, Trash2, Activity, Crown, AlertTriangle, Users } from 'lucide-react';
import { teamApi } from '../../services/account';
import { Badge, Button, Modal, Notice, formatWhen } from './ui';
import { CredentialsBox } from './InviteMemberModal';

const STATUS = {
  active: { label: 'Active', tone: 'green' },
  invited: { label: 'Invited', tone: 'blue' },
  expired: { label: 'Link expired', tone: 'amber' },
  suspended: { label: 'Suspended', tone: 'red' },
};

const RowMenu = ({ member, myRole, onAction }) => {
  const [open, setOpen] = useState(false);
  const canManage = myRole === 'owner' || member.role === 'member';
  const item = (icon, label, action, danger = false) => (
    <button
      type="button"
      onClick={() => { setOpen(false); onAction(action, member); }}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-slate-800 ${danger ? 'text-red-600' : 'text-gray-700 dark:text-slate-200'}`}
    >
      {icon}{label}
    </button>
  );
  if (!canManage) return null;
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800" aria-label="More actions">
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1">
            {item(<Activity className="w-4 h-4" />, 'View activity', 'activity')}
            {item(<Send className="w-4 h-4" />, member.status === 'active' ? 'Reset password & resend login' : 'Resend invite', 'resend')}
            {myRole === 'owner' && item(<Crown className="w-4 h-4" />, member.role === 'co_admin' ? 'Make member' : 'Make co-admin', 'role')}
            {member.status === 'suspended'
              ? item(<PlayCircle className="w-4 h-4" />, 'Reactivate', 'reactivate')
              : item(<PauseCircle className="w-4 h-4" />, 'Suspend', 'suspend')}
            {item(<Trash2 className="w-4 h-4" />, 'Remove from team', 'remove', true)}
          </div>
        </>
      )}
    </div>
  );
};

const MembersTab = ({ data, options, myRole, onReload, onInvite, onAuthority, onBulk, onViewActivity, flash }) => {
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const members = useMemo(() => data?.members || [], [data]);
  const selectable = members.filter((m) => m.role === 'member');
  const seats = data?.seats || {};
  const seatsFull = seats.limit != null && seats.used >= seats.limit;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allChecked = selectable.length > 0 && selectable.every((m) => selected.has(m.id));

  const run = async (fn, okMessage) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      if (res?.data?.credentials) setCredentials({ ...res.data.credentials, message: res.message });
      flash(okMessage || res?.message || 'Done');
      await onReload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const onAction = (action, m) => {
    if (action === 'activity') return onViewActivity(m.id);
    if (action === 'resend') return setConfirm({ action, member: m });
    if (action === 'role') return run(() => teamApi.updateMember(m.id, { role: m.role === 'co_admin' ? 'member' : 'co_admin' }),
      `${m.name || m.email} is now ${m.role === 'co_admin' ? 'a member' : 'a co-admin'}`);
    if (action === 'suspend') return setConfirm({ action, member: m });
    if (action === 'reactivate') return run(() => teamApi.updateMember(m.id, { status: 'active' }), `${m.name || m.email} reactivated`);
    if (action === 'remove') return setConfirm({ action, member: m });
    return undefined;
  };

  const confirmText = confirm && {
    resend: {
      title: 'Send new login details?',
      body: `${confirm.member.name || confirm.member.email} gets a new temporary password and a new sign-in link. Their current password and any earlier link stop working.`,
      button: 'Send',
      run: () => teamApi.resendInvite(confirm.member.id),
    },
    suspend: {
      title: 'Suspend this person?',
      body: `${confirm.member.name || confirm.member.email} is signed out straight away and can't sign in until you reactivate them. Their pipeline and settings are kept.`,
      button: 'Suspend',
      run: () => teamApi.updateMember(confirm.member.id, { status: 'suspended' }),
    },
    remove: {
      title: 'Remove from the team?',
      body: `${confirm.member.name || confirm.member.email}'s login is deleted and their seat is freed. Their pipeline items move to your pipeline and their sent emails stay in the account's history. This can't be undone.`,
      button: 'Remove',
      run: () => teamApi.removeMember(confirm.member.id),
    },
  }[confirm.action];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
          <Users className="w-4 h-4 text-gray-400" />
          {seats.limit == null
            ? `${seats.used || 0} team members (no seat limit)`
            : `${seats.used || 0} of ${seats.limit} team seats used`}
        </div>
        <Button onClick={onInvite} disabled={seatsFull} title={seatsFull ? 'All seats are in use' : undefined}>
          <UserPlus className="w-4 h-4" /> Add person
        </Button>
      </div>

      {seatsFull && (
        <Notice tone="amber">
          {seats.limit === 0
            ? `Your ${options?.plan || 'current'} plan doesn't include team members. Upgrade your plan to add people.`
            : 'All your team seats are in use. Remove someone or upgrade your plan to add more people.'}
        </Notice>
      )}
      {error && <Notice>{error}</Notice>}
      {credentials && (
        <div className="space-y-2">
          <Notice tone="amber">{credentials.message}</Notice>
          <CredentialsBox credentials={credentials} />
          <Button variant="ghost" onClick={() => setCredentials(null)}>Hide</Button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 px-4 py-2.5">
          <span className="text-sm text-blue-800 dark:text-blue-300">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
            <Button onClick={() => onBulk([...selected])}><ShieldCheck className="w-4 h-4" /> Give them the same authority</Button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-slate-400 border-b border-gray-200 dark:border-slate-700">
              <th className="px-4 py-3 w-10">
                <input type="checkbox" aria-label="Select all members" checked={allChecked} disabled={!selectable.length}
                  onChange={() => setSelected(allChecked ? new Set() : new Set(selectable.map((m) => m.id)))} />
              </th>
              <th className="px-2 py-3 font-medium">Person</th>
              <th className="px-2 py-3 font-medium">Role</th>
              <th className="px-2 py-3 font-medium">Status</th>
              <th className="px-2 py-3 font-medium">Template</th>
              <th className="px-2 py-3 font-medium">Brands</th>
              <th className="px-2 py-3 font-medium">Last active</th>
              <th className="px-2 py-3 font-medium text-right pr-4">Authority</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
            {data?.owner && (
              <tr className="bg-gray-50/50 dark:bg-slate-800/30">
                <td className="px-4 py-3" />
                <td className="px-2 py-3">
                  <div className="font-medium text-gray-900 dark:text-slate-100">{data.owner.name}</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">{data.owner.email}</div>
                </td>
                <td className="px-2 py-3"><Badge tone="purple"><Crown className="w-3 h-3" />Owner</Badge></td>
                <td className="px-2 py-3"><Badge tone="green">Active</Badge></td>
                <td className="px-2 py-3 text-gray-400">—</td>
                <td className="px-2 py-3 text-gray-500 dark:text-slate-400">All</td>
                <td className="px-2 py-3 text-gray-400">—</td>
                <td className="px-2 py-3 pr-4 text-right text-xs text-gray-400">Full access</td>
              </tr>
            )}
            {members.map((m) => {
              const st = STATUS[m.status] || STATUS.invited;
              return (
                <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/40">
                  <td className="px-4 py-3">
                    {m.role === 'member' && <input type="checkbox" aria-label={`Select ${m.name}`} checked={selected.has(m.id)} onChange={() => toggle(m.id)} />}
                  </td>
                  <td className="px-2 py-3">
                    <div className="font-medium text-gray-900 dark:text-slate-100">{m.name || m.email}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">{m.email}</div>
                  </td>
                  <td className="px-2 py-3">
                    {m.role === 'co_admin' ? <Badge tone="purple">Co-admin</Badge> : <Badge>Member</Badge>}
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge tone={st.tone}>{st.label}</Badge>
                      {m.status !== 'active' && ['failed', 'skipped'].includes(m.inviteEmailStatus) && (
                        <Badge tone="red" title="The invite email could not be sent. Use Resend invite.">
                          <AlertTriangle className="w-3 h-3" />Email not sent
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-3 text-gray-600 dark:text-slate-300">{m.role === 'co_admin' ? '—' : (m.templateName || <span className="text-gray-400">Custom</span>)}</td>
                  <td className="px-2 py-3 text-gray-600 dark:text-slate-300">
                    {m.role === 'co_admin' || !m.permissions
                      ? 'All'
                      : ['projects', 'competitors', 'gaps'].every((k) => m.permissions.brands?.[k]?.see === 'all')
                        ? 'All'
                        : `${m.brandCount} selected`}
                  </td>
                  <td className="px-2 py-3 text-gray-500 dark:text-slate-400">{formatWhen(m.lastActiveAt || m.lastLoginAt)}</td>
                  <td className="px-2 py-3 pr-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onAuthority(m)}
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                        title="Authority — what they can see and do"
                      >
                        <ShieldCheck className="w-4 h-4" />
                        <span className="text-xs font-medium">Authority</span>
                      </button>
                      <RowMenu member={m} myRole={myRole} onAction={onAction} />
                    </div>
                  </td>
                </tr>
              );
            })}
            {!members.length && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-slate-400">
                  No one else is on your team yet. Add a person to give them their own login.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={Boolean(confirm)}
        title={confirmText?.title}
        onClose={() => setConfirm(null)}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant={confirm?.action === 'resend' ? 'primary' : 'danger'} disabled={busy} onClick={() => run(confirmText.run)}>
              {busy ? 'Working…' : confirmText?.button}
            </Button>
          </>
        )}
      >
        <p className="text-sm text-gray-600 dark:text-slate-300">{confirmText?.body}</p>
      </Modal>
    </div>
  );
};

export default MembersTab;
